import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import mikoCharacter from "@/assets/miko-character.png";
import klanggeschichtenImg from "@/assets/miko/klanggeschichten-zauber.jpg";
import aktionstablettImg from "@/assets/miko/aktionstablett-generator.jpg";
import geraeuscheSafariImg from "@/assets/miko/geraeusche-safari.jpg";
import geschichtensaeckchenImg from "@/assets/miko/geschichtensaeckchen-erfinder.jpg";
import rhythmusDetektiveImg from "@/assets/miko/rhythmus-detektive.jpg";
import naturmaterialienImg from "@/assets/miko/musizieren-naturmaterialien.jpg";
import SEO from "@/components/SEO";
import { 
  Sparkles, 
  MessageSquare, 
  Lightbulb, 
  Music, 
  Wand2, 
  BookOpen,
  ArrowRight,
  CheckCircle2
} from "lucide-react";

const features = [
  {
    icon: <Wand2 className="w-6 h-6" />,
    title: "KI-Tools",
    description: "Von Klanggeschichten bis Rhythmus-Detektive – kreative Ideen auf Knopfdruck für deinen pädagogischen Alltag."
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: "Intelligenter Chatbot",
    description: "Dein persönlicher Assistent für alle Fragen rund um Musik in der Kita – mit Zugriff auf MusiKonzept-Wissen."
  },
  {
    icon: <BookOpen className="w-6 h-6" />,
    title: "Musik-Bibliothek",
    description: "Durchsuche hunderte Lieder, Fingerspiele und Bewegungsideen – sortiert nach Thema, Alter und Anlass."
  },
  {
    icon: <Music className="w-6 h-6" />,
    title: "Musik-Tools",
    description: "Akkord-Player, virtuelle Gitarre, Transponier-Assistent und mehr – alles direkt im Browser."
  }
];

const toolPreviews = [
  { name: "Klanggeschichten-Zauber", image: klanggeschichtenImg },
  { name: "Aktionstablett-Generator", image: aktionstablettImg },
  { name: "Geräusche-Safari", image: geraeuscheSafariImg },
  { name: "Geschichtensäckchen-Erfinder", image: geschichtensaeckchenImg },
  { name: "Rhythmus-Detektive", image: rhythmusDetektiveImg },
  { name: "Musik mit Naturmaterialien", image: naturmaterialienImg },
];

const Miko = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO title={`Miko – KI-Lernbegleitung für Musikpädagogik | MusiKonzept`} description={`Miko ist die KI-Lernbegleitung von MusiKonzept – persönliche Unterstützung bei Praxisideen, Liedern und Konzepten.`} path="/miko" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                KI-gestützte Musikpädagogik
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Miko – Dein<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  KI-Assistent
                </span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Entdecke die Zukunft der Musikpädagogik: Mit Miko erstellst du in Sekunden 
                kreative Ideen, findest passende Lieder und bekommst Antworten auf alle 
                Fragen rund um Musik mit Kindern.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <NavLink to="/registrieren">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-primary to-accent text-white font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 w-full sm:w-auto"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Jetzt entdecken
                  </Button>
                </NavLink>
                <NavLink to="/preise">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </NavLink>
              </div>
            </div>
            
            {/* Miko Character */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl scale-110" />
                <img 
                  src={mikoCharacter} 
                  alt="Miko - Dein KI-Assistent" 
                  className="relative w-64 md:w-80 lg:w-96 drop-shadow-2xl"
                />
                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 bg-white dark:bg-card rounded-xl shadow-lg p-3 animate-bounce">
                  <Lightbulb className="w-6 h-6 text-accent" />
                </div>
                <div className="absolute bottom-10 -left-8 bg-white dark:bg-card rounded-xl shadow-lg p-3 animate-pulse">
                  <Music className="w-6 h-6 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Was kann Miko?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Miko vereint künstliche Intelligenz mit musikpädagogischem Fachwissen – 
              speziell entwickelt für den Kita-Alltag.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card rounded-2xl p-6 border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Tools Preview */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Viele KI-Tools für deinen Alltag
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Egal ob du eine Klanggeschichte zum Thema Herbst brauchst, Ideen für 
              musikalische Aktionstabletts suchst oder einen Rhythmus-Workshop planst – 
              Miko hilft dir dabei.
            </p>
          </div>
          
          {/* Tool Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-10">
            {toolPreviews.map((tool, index) => (
              <div 
                key={index}
                className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
              >
                <img 
                  src={tool.image} 
                  alt={tool.name}
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h4 className="text-white font-semibold text-sm md:text-base">
                    {tool.name}
                  </h4>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <NavLink to="/registrieren">
              <Button size="lg" className="bg-gradient-to-r from-primary to-accent text-white">
                Alle Tools entdecken
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </NavLink>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-6 text-center">
          <img 
            src={mikoCharacter} 
            alt="Miko" 
            className="w-24 h-24 mx-auto mb-6"
          />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Bereit für Miko?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Starte jetzt kostenlos und entdecke, wie KI deinen musikpädagogischen Alltag bereichern kann.
          </p>
          <NavLink to="/registrieren">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-primary to-accent text-white font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Kostenlos registrieren
            </Button>
          </NavLink>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Miko;
